import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { externalApiService } from 'services/service';

export const useGetReports = (salespersonEmail, customerEmail) => {
    const [reports, setReports] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const perPage = 10;

    const fetchReports = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (salespersonEmail) {
                params.append('salesperson_email', salespersonEmail);
            }
            if (customerEmail) {
                params.append('customer_email', customerEmail);
            }
            params.append('page', '1');
            params.append('per_page', perPage.toString());
            params.append('allow', 'tajiro');
            const queryString = params.toString();
            const url = `/api/get-reports?${queryString}`;
            const response = await externalApiService.get(url);
            setReports(response?.data || []);
            setPagination(response?.info);
            setTotalCount(response?.info?.count || 0);
            setHasMore(response?.info?.more_records || false);
            setCurrentPage(1);
            return response?.data?.data;
        } catch (error) {
            if (error?.message?.includes('Something went wrong')) {
                setError('Unable to load reports at this time. Please try again later.');
            } else if (error?.message?.includes('Token expired')) {
                setError('Session expired. Please log in again.');
                toast.error('Session expired. Please log in again.');
            } else {
                setError('Failed to load reports. Please check your connection.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const loadMore = async () => {
        if (!hasMore || isLoading) return;
        setIsLoading(true);
        try {
            const nextPage = currentPage + 1;
            const params = new URLSearchParams();
            if (salespersonEmail) {
                params.append('salesperson_email', salespersonEmail);
            }
            if (customerEmail) {
                params.append('customer_email', customerEmail);
            }
            params.append('page', nextPage.toString());
            params.append('per_page', perPage.toString());
            params.append('allow', 'tajiro');
            const queryString = params.toString();
            const url = `/api/get-reports?${queryString}`;
            const response = await externalApiService.get(url);
            const newReports = response?.data || [];
            setReports((prev) => [...prev, ...newReports]);
            setPagination(response?.data?.info);
            setTotalCount(response?.data?.info?.count || 0);
            setHasMore(response?.data?.info?.more_records || false);
            setCurrentPage(nextPage);
        } catch (error) {
            if (error?.message?.includes('Something went wrong')) {
                setError('Unable to load more reports at this time. Please try again later.');
            } else if (error?.message?.includes('Token expired')) {
                setError('Session expired. Please log in again.');
                toast.error('Session expired. Please log in again.');
            } else {
                setError('Failed to load more reports. Please check your connection.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (salespersonEmail || customerEmail) {
            fetchReports();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salespersonEmail, customerEmail]);

    return {
        reports,
        isLoading,
        pagination,
        error,
        refetch: fetchReports,
        loadMore,
        hasMore,
        totalCount,
    };
};

export const useReportMutations = () => {
    const [isPending, setIsPending] = useState(false);

    const create = async (data) => {
        setIsPending(true);
        try {
            const response = await externalApiService.post({
                url: '/api/create-report?allow=tajiro',
                payload: data,
            });
            return {
                success: true,
                data: response?.data,
                response: response,
            };
        } catch (error) {
            console.error('Error from creating report:', error);
            const errorMsg = error?.message || 'Error creating report';
            if (!errorMsg.includes('Something went wrong')) {
                toast.error(errorMsg);
            } else {
                toast.error('Failed to create report. Please try again.');
            }
            return {
                success: false,
                error: error,
                message: errorMsg,
            };
        } finally {
            setIsPending(false);
        }
    };

    const update = async (data) => {
        setIsPending(true);
        try {
            const response = await externalApiService.put({
                url: '/api/update-report?allow=tajiro',
                payload: data,
            });
            return {
                success: true,
                data: response?.data,
                response: response,
            };
        } catch (error) {
            console.error('Error from updating report:', error);
            const errorMsg = error?.message || 'Error updating report';
            if (!errorMsg.includes('Something went wrong')) {
                toast.error(errorMsg);
            } else {
                toast.error('Failed to update report. Please try again.');
            }
            return {
                success: false,
                error: error,
                message: errorMsg,
            };
        } finally {
            setIsPending(false);
        }
    };

    return { create, update, isPending };
};
